import { memo, useCallback, useRef, useState } from 'react';
import { TimelineWrapper } from 'components/styled/scoped';
import { useTranslate } from '@tolgee/react';
import { css } from '@emotion/css';
import { Timeline, Spin, Drawer } from 'antd';
import dayjs from 'lib/dayjs';
import { useGetSpuMarkRecord } from './connect';

type MarkRecordProps = {
  spuId: string;
  stockRuleId: string;
  visible: boolean;
  onCancel: () => void;
};

export const useRenderRecord = () => {
  const { t } = useTranslate();
  return (each: OutofStock.MarkRecord) => {
    const userName = each.operateUserId === -1 ? t('cancelType.system') : each.operateUserName;
    if (each.operateType === 'ADD_RULE') {
      return t('outofstock_record_mark_action', { x: userName });
    }
    if (each.operateType === 'DEL_RULE') {
      return t('outofstock_record_cancel_action', { x: userName });
    }
    if (each.operateType === 'AUTO_DEL_RULE') {
      return t('outofstock_record_cancel_action', {
        x: t('cancelType.system'),
      });
    }
    if (each.operateType === 'DEL_EATIME') {
      return t('outofstock_record_edittime_action2', { x: userName });
    }
    if (each.operateType === 'UPDATE_EATIME') {
      return t('outofstock_record_edittime_action', {
        x: userName,
        y: each.estimatedAvailableTime,
      });
    }
    return '';
  };
};

const MarkRecord = memo((props: MarkRecordProps) => {
  const { spuId, stockRuleId, visible, onCancel } = props;
  const { t } = useTranslate();
  const { data, loading } = useGetSpuMarkRecord(stockRuleId);

  const renderRecord = useRenderRecord();

  return (
    <Drawer
      title={t('outofstock_who_record', { x: spuId })}
      width={558}
      visible={visible}
      onClose={onCancel}
      footer={null}
      className={css`
        .ant-modal-body {
          padding: 15px 22px !important;
          margin: 0 !important;
        }
      `}
    >
      {loading ? (
        <div className="flex justify-center items-center h-[200px]">
          <Spin />
        </div>
      ) : (
        <div>
          <div className="leading-[20px] text-[var(--fett-text-tertiary)] mb-[12px]">
            {t('outofstock_recent_record', { x: 6 })}
          </div>
          <TimelineWrapper>
            <Timeline>
              {data?.map((e, i) => {
                return (
                  <Timeline.Item key={i}>
                    <div className="flex items-center justify-between">
                      <div className="var(--fett-text-primary)">{renderRecord(e)}</div>
                      <div className="text-[12px] text-[var(--fett-text-tertiary)]">
                        {dayjs(Number(e.createTimeMs)).format('YYYY-MM-DD HH:mm:ss')}
                      </div>
                    </div>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </TimelineWrapper>
        </div>
      )}
    </Drawer>
  );
});

const useMarkRecord = () => {
  const [visible, setVisible] = useState(false);
  const spuIdRef = useRef<string>('');
  const stockRuleIdRef = useRef<string>('');
  const open = useCallback((spuId: string, stockRuleId: string) => {
    spuIdRef.current = spuId;
    stockRuleIdRef.current = stockRuleId;
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
  }, []);

  const instance = visible && (
    <MarkRecord
      spuId={spuIdRef.current}
      stockRuleId={stockRuleIdRef.current}
      visible={visible}
      onCancel={close}
    />
  );

  return [open, instance] as const;
};

export default useMarkRecord;
